import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { Flex } from '@react-css/flex'
import { ErrorBoundary } from 'react-error-boundary'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Switch, Route, Link, BrowserRouter, Redirect } from 'react-router-dom'
import styled from 'styled-components'

import imgLogo from '../../assets/images/logo.png'
import {
  AdminZoneProvider,
  useAdminZoneContext,
} from '../../contexts/adminZoneContext'
import { BuildingProvider } from '../../contexts/buildingContext'
import { NotificationsProvider } from '../../contexts/notificationProvider'
import { NotificationSystem } from '../../contexts/NotificationSystem'
import { ProgressReportJobsProvider } from '../../contexts/progressReportJobsContext'
import { SafetyReportJobsProvider } from '../../contexts/safetyReportJobsContext'
import { UserProvider, useUserContext } from '../../contexts/userContext'
import { PATH_STRINGS } from '../../hooks/useGeneratedPaths'
import { media } from '../../styles/breakpoints'
import { saveAuthReturnPath } from '../../utils/authReturnPath'
import { AppErrorFallback } from '../common/Errors/AppErrorFallback'
import { LoadingIndicator } from '../common/LoadingIndicator'
import { MobileFloorMapUnavailableProvider } from '../common/MobileFloorMapUnavailable/MobileFloorMapUnavailableProvider'
import { PageViewHandler } from '../common/PageViewHandler'
import { PermissionRoute } from '../common/PermissionRoute/PermissionRoute'
import { ProgressReportJobBanner } from '../common/ProgressReportJobBanner'
import { SafetyReportJobBanner } from '../common/SafetyReportJobBanner'
import { UserBlock } from '../common/UserBlock'

import { AdminBuildingPage } from './admin_building_page/AdminBuildingPage'
import { BuildingPage } from './building_page/BuildingPage'
import { BuildingPageHeaderOutlet } from './building_page/components/BuildingPageHeaderOutlet'
import { BuildingsPage } from './buildings_page/BuildingsPage'
import { LoginPage } from './login/Login'
import { LoginRedirect } from './login/LoginRedirect'
import { RequestResetPage } from './login/RequestReset'
import { ResetPasswordPage } from './login/ResetPassword'
import { SiteWalkPage } from './site_walk/SiteWalkPage'
import { UserProfile } from './user_profile/UserProfile'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 5000,
      refetchOnWindowFocus: false,
    },
  },
})

interface AppProps {
  msalInstance: PublicClientApplication
}

function App({ msalInstance }: AppProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <ErrorBoundary
          FallbackComponent={AppErrorFallback}
          onReset={() => {
            window.location.reload()
          }}
        >
          <NotificationsProvider>
            <UserProvider>
              <SafetyReportJobsProvider>
                <ProgressReportJobsProvider>
                  <PageViewHandler />
                  <QueryClientProvider client={queryClient}>
                    <MobileFloorMapUnavailableProvider>
                      <AdminZoneProvider>
                        <div>
                          <Header>
                            <HeaderLeft>
                              <HeaderBrandWrap>
                                <LogoWithAdminIndicator />
                              </HeaderBrandWrap>
                            </HeaderLeft>
                            <HeaderCenter>
                              <Switch>
                                <Route
                                  path={[
                                    '/login',
                                    '/redirect',
                                    '/request-reset',
                                    '/reset-password',
                                  ]}
                                  exact
                                >
                                  <></>
                                </Route>
                                <PrivateRoute
                                  path="/admin/project/:projectId"
                                  component={BuildingPageHeaderOutlet}
                                  fbLoader={true}
                                />
                                <PrivateRoute
                                  path="/project/:projectId"
                                  component={BuildingPageHeaderOutlet}
                                  fbLoader={true}
                                />

                                <PrivateRoute
                                  exact
                                  path="/admin/project/:projectId/floor/:floorId"
                                  fbLoader={true}
                                  component={BuildingPageHeaderOutlet}
                                />
                                <PrivateRoute
                                  exact
                                  path="/project/:projectId/floor/:floorId"
                                  fbLoader={true}
                                  component={BuildingPageHeaderOutlet}
                                />

                                <PrivateRoute
                                  path="/"
                                  component={UserCompanyLogo}
                                  fbLoader={true}
                                />
                              </Switch>
                            </HeaderCenter>
                            <HeaderRight>
                              <Flex
                                style={{
                                  flexDirection: 'column',
                                  alignItems: 'flex-end',
                                  marginRight: 8,
                                  gap: 2,
                                }}
                              >
                                <SafetyReportJobBanner />
                                <ProgressReportJobBanner />
                              </Flex>
                              <Flex
                                style={{ width: '125px' }}
                                alignItems={'center'}
                                justifyContent={'flex-end'}
                              >
                                <UserBlock />
                              </Flex>
                            </HeaderRight>
                          </Header>
                          <Content id="content">
                            <Switch>
                              <Route path="/redirect" exact>
                                <LoginRedirect />
                              </Route>
                              <Route path="/login" exact>
                                <LoginPage />
                              </Route>
                              <Route path="/request-reset" exact>
                                <RequestResetPage />
                              </Route>
                              <Route path="/reset-password" exact>
                                <ResetPasswordPage />
                              </Route>
                              <PrivateRoute
                                path={PATH_STRINGS.userProfile}
                                component={UserProfile}
                              />
                              <PrivateRoute
                                path={PATH_STRINGS.siteWalk}
                                component={SiteWalkPage}
                              />
                              <PermissionRoute
                                permission="project_tables.admin_zone_access"
                                path="/admin"
                                exact
                                component={BuildingsPage}
                              />
                              <PrivateRoute
                                path="/"
                                exact
                                component={BuildingsPage}
                              />

                              <PermissionRoute
                                permission="project_tables.admin_read_project"
                                path={PATH_STRINGS.adminProject}
                                component={WrappedAdminBuildingPage}
                              />
                              <PermissionRoute
                                permission="project_tables.read_project"
                                path={PATH_STRINGS.project}
                                component={WrappedBuildingPage}
                              />
                            </Switch>
                          </Content>
                        </div>
                      </AdminZoneProvider>
                    </MobileFloorMapUnavailableProvider>

                    <NotificationSystem />
                  </QueryClientProvider>
                </ProgressReportJobsProvider>
              </SafetyReportJobsProvider>
            </UserProvider>
          </NotificationsProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </MsalProvider>
  )
}

const WrappedBuildingPage = () => (
  <BuildingProvider>
    <BuildingPage />
  </BuildingProvider>
)

const WrappedAdminBuildingPage = () => (
  <BuildingProvider>
    <AdminBuildingPage />
  </BuildingProvider>
)

function PrivateRoute({
  component: Component,
  fbLoader = false,
  ...rest
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Replace with proper rest type
}: any) {
  const { state: authState } = useUserContext()
  if (authState.loading) return <LoadingIndicator fb={fbLoader} />
  return (
    <Route
      {...rest}
      render={props => {
        if (authState.isAuthenticated) {
          return <Component {...props} />
        }

        saveAuthReturnPath(props.location)
        return (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: props.location },
            }}
          />
        )
      }}
    />
  )
}

const Header = styled.div`
  position: relative;
  z-index: 1100;
  min-height: 68px;
  overflow: visible;
  background: #ffffff;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.05);

  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;

  padding-left: 60px;
  padding-right: 60px;

  @media ${media.belowTablet} {
    height: auto;
    min-height: 56px;
    padding: 10px 16px;
    grid-template-columns: minmax(48px, max-content) 1fr 88px;
    gap: 4px 8px;
  }
`

const HeaderLeft = styled.div`
  justify-self: start;
`

const HeaderBrandWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100px;

  @media ${media.belowTablet} {
    width: auto;
    max-width: 72px;
  }
`

const HeaderCenter = styled.div`
  justify-self: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${media.belowTablet} {
    justify-self: stretch;
    text-align: center;
  }
`

const HeaderRight = styled.div`
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: visible;

  @media ${media.belowTablet} {
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }
`

const Logo = styled.img.attrs(() => ({
  src: imgLogo,
}))`
  width: 36px;
  height: 31px;
  opacity: 0.4;
  margin-top: -1px;
`

const LogoWithAdminIndicator = () => {
  const { inAdminZone } = useAdminZoneContext()

  return (
    <LogoAdminWrap $inAdminZone={inAdminZone}>
      <Link to="/">
        <Logo />
      </Link>

      {inAdminZone && (
        <Switch>
          <Route path="/admin">
            <Link to="/admin">
              <AdminLabel>Admin</AdminLabel>
            </Link>
          </Route>
        </Switch>
      )}
    </LogoAdminWrap>
  )
}

const LogoAdminWrap = styled.div<{ $inAdminZone?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;

  @media ${media.belowTablet} {
    flex-direction: ${props => (props.$inAdminZone ? 'column' : 'row')};
    align-items: flex-start;
    gap: 2px;
  }
`

const AdminLabel = styled.span`
  color: #7e858e;
  margin-left: 10px;
  font-size: 14px;
  white-space: nowrap;

  @media ${media.belowTablet} {
    margin-left: 0;
    font-size: 11px;
    line-height: 1;
  }
`

const UserCompanyLogo = () => {
  const { state: authState } = useUserContext()

  if (authState.is_demo_account) {
    return null
  }

  const employer = authState.employer as
    | { logo_url?: string | null; name?: string }
    | undefined
  if (!employer) {
    return null
  }

  if (employer.logo_url) {
    return <CompanyLogoImage alt="" src={employer.logo_url} />
  }

  if (!employer.name) {
    return null
  }

  return (
    <CompanyLogoText title={employer.name}>{employer.name}</CompanyLogoText>
  )
}

const CompanyLogoImage = styled.img`
  height: auto;
  max-height: 30px;
  max-width: 150px;
  object-fit: contain;

  @media ${media.belowTablet} {
    display: none;
  }
`

const CompanyLogoText = styled.span`
  color: #7e858e;
  font-size: 15px;
  font-weight: 500;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${media.belowTablet} {
    display: none;
  }
`

const Content = styled.div`
  position: absolute;
  right: 60px;
  left: 60px;
  top: 68px;
  bottom: 0;

  @media ${media.belowTablet} {
    right: 16px;
    left: 16px;
    top: 72px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
`
export default App

if (!Object.prototype.hasOwnProperty.call(String.prototype, 'capitalize')) {
  Object.defineProperty(String.prototype, 'capitalize', {
    value: function () {
      return this.charAt(0).toUpperCase() + this.slice(1)
    },
    enumerable: false,
  })
}
